import React, { useState } from 'react';
import ActivitySection from './ActivitySection';
import SectionState from './SectionState';
import TranscriptView from './TranscriptView';
import { ShowMore, usePagedItems } from './usePagedItems';
import { dayMonth, plural } from './lib/activityFormat';

const rungsText = (r) => `${r.strong} strong, ${r.adequate} adequate, ${r.weak} weak`;

/** Strong / adequate / weak as a stacked bar, with the counts in words beside it. */
export const Rungs = ({ rungs }) => {
  const total = rungs.strong + rungs.adequate + rungs.weak;
  const width = (n) => `${total ? (n / total) * 100 : 0}%`;
  return (
    <span className="td-act-rungs-wrap">
      <span className="td-act-rungs" aria-hidden="true">
        <i className="s" style={{ width: width(rungs.strong) }} />
        <i className="a" style={{ width: width(rungs.adequate) }} />
        <i className="w" style={{ width: width(rungs.weak) }} />
      </span>
      <span className="td-act-rungs__text">{rungsText(rungs)}</span>
    </span>
  );
};

function runMeta(run) {
  const parts = [plural(run.turns, 'turn')];
  if (run.reached_extension) parts.push('reached the extension question');
  if (run.hints_given != null) parts.push(run.hints_given ? plural(run.hints_given, 'hint') : 'no hints');
  if (run.ended_label) parts.push(run.ended_label);
  return parts.join(' · ');
}

const RunRow = ({ run, studentId, open, onToggle }) => {
  const transcriptId = `interview-${run.id}-transcript`;
  return (
    <li className="td-act-run">
      <div className="td-act-rowline">
        <span className="td-act-when">{dayMonth(run.started_at)}</span>
        <div className="td-act-rowline__main">
          <div className="td-act-rowtitle">{run.subtitle ? `${run.title} · ${run.subtitle}` : run.title}</div>
          <div className="td-act-rowsub">{runMeta(run)}</div>
        </div>
        {run.rungs ? <Rungs rungs={run.rungs} /> : <span className="td-act-meta">Not marked</span>}
        <button
          type="button"
          className="td-btn td-btn--small"
          aria-expanded={open}
          aria-controls={transcriptId}
          onClick={onToggle}
        >
          {open ? 'Hide transcript' : 'Transcript'}
        </button>
      </div>
      {open ? <TranscriptView studentId={studentId} runId={run.id} id={transcriptId} /> : null}
    </li>
  );
};

/**
 * Interviews, from GET …/activity/interviews: each live mock interview with
 * its turns, hints, how it ended and how its questions were marked, and its
 * transcript on demand.
 */
const InterviewSection = ({ state, studentId, fetchPage }) => {
  const first = state.status === 'ready' ? state.data : null;
  const paged = usePagedItems(first, 'runs', fetchPage);
  const [open, setOpen] = useState(null);

  return (
    <ActivitySection
      id="interviews"
      title="Interviews"
      description="Live mock interviews. Each question is marked strong, adequate or weak."
    >
      <SectionState state={state} what="interviews" emptyTitle="No interviews in this period" />
      {first ? (
        <div className="td-card">
          <ul className="td-act-rows">
            {paged.items.map((run) => (
              <RunRow
                key={run.id}
                run={run}
                studentId={studentId}
                open={open === run.id}
                onToggle={() => setOpen((cur) => (cur === run.id ? null : run.id))}
              />
            ))}
          </ul>
          <div className="td-act-legend">
            <span className="td-act-legend__s">Strong</span>
            <span className="td-act-legend__a">Adequate</span>
            <span className="td-act-legend__w">Weak</span>
          </div>
          <ShowMore paged={paged} what="interviews" />
        </div>
      ) : null}
    </ActivitySection>
  );
};

export default InterviewSection;
